import { useState, type ReactNode } from 'react';
import { HelpCircle, X } from 'lucide-react';

/**
 * Sprint 95 — Una tarjeta de acción que puede darse la vuelta para explicarse.
 *
 * Idea del dueño del producto:
 *
 * > *"Se me ocurre que los cards tengan un pequeño ícono que sirva para voltear la card, y en la
 * > card tenga indicaciones de cómo se usa la propia card."*
 *
 * ## Por qué aquí sí vale la pena, y por qué normalmente no
 *
 * Una tarjeta que necesita instrucciones suele ser una tarjeta mal diseñada, y el reverso puede
 * volverse la muleta que permite publicar interfaces confusas. Aquí es distinto por una razón
 * concreta: **la mitad de la acción ocurre fuera del sistema**. Copiar un enlace y pegarlo en
 * WhatsApp, esperar a que el grupo conteste, mirar la notificación del banco — nada de eso lo puede
 * enseñar una pantalla por sí sola, por bien dibujada que esté.
 *
 * Dos reglas que este componente se impone para no convertirse en esa muleta:
 *
 * 1. **La cara de enfrente funciona sin voltear nunca.** El reverso es para la primera semana, no
 *    para todos los días. Si alguien *necesita* leerlo para trabajar, la tarjeta está mal.
 * 2. **Voltear no puede ser un accidente.** El botón es chico y está en una esquina; el resto de la
 *    tarjeta —incluida el área grande— dispara la acción de verdad. En un POS con el cliente
 *    esperando, una animación que tapa el botón útil es un estorbo.
 */
export function TarjetaConReverso({
  titulo,
  descripcion,
  icono,
  acento,
  onAccion,
  textoAccion,
  pie,
  comoSeUsa,
  alPie,
}: {
  titulo: string;
  descripcion: string;
  icono: ReactNode;
  /** Clases de color del acento. Cada tarjeta tiene el suyo para reconocerse de lejos. */
  acento: { borde: string; fondo: string; texto: string; boton: string };
  onAccion: () => void;
  textoAccion: string;
  /** Debajo del botón: el estado de la acción ("✓ copiado", "3 hoy"). */
  pie?: ReactNode;
  /** Los pasos del reverso, en el orden en que ocurren de verdad. */
  comoSeUsa: string[];
  /** Sprint 96 — Una acción discreta al pie (apagar el enlace, por ejemplo). */
  alPie?: { texto: string; onClick: () => void | Promise<void> };
}) {
  const [volteada, setVolteada] = useState(false);

  return (
    <div className="[perspective:1200px]">
      {/*
        Sprint 117 — La altura la manda EL FRENTE; el reverso se confina.

        Antes las DOS caras eran `absolute inset-0` dentro de un contenedor con `min-h-[188px]`
        fijo. Sacar del flujo lo único que tiene contenido deja al contenedor sin altura propia:
        medía 188 px pasara lo que pasara, y la tarjeta con un enlace de más al pie —"Apagar el
        menú en línea"— se salía por abajo y **se encimaba con los encabezados de las columnas**:
        "Llegaron del enlace" ilegible debajo de "Compartir el menú en línea".

        El primer intento puso ambas caras en una rejilla de una celda. Arregló el encimado y
        **empeoró lo que el dueño había reportado**: la celda toma la altura de la más alta, y el
        reverso —seis pasos de "cómo se usa"— le imponía su altura al frente aunque estuviera
        oculto. Las tarjetas pasaron de un cuarto de pantalla a casi la mitad.

        Lo que queda: el **frente** va en el flujo normal y define la altura —la de su propio
        contenido, sin mínimos inventados—; el **reverso** vuelve a `absolute inset-0`, que es
        justo lo que necesita para superponerse sin aportar altura, y lleva `overflow-auto` para
        que su lista larga se desplace dentro de la tarjeta en vez de desbordarse.
      */}
      <div
        className={`relative transition-transform duration-500 [transform-style:preserve-3d] ${
          volteada ? '[transform:rotateY(180deg)]' : ''
        }`}
      >
        {/* ─── Frente: la acción ─── */}
        <div
          className={`flex flex-col rounded-2xl border-2 p-3 [backface-visibility:hidden] ${acento.borde} ${acento.fondo}`}
          aria-hidden={volteada}
        >
          <div className="flex items-start justify-between gap-2">
            <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${acento.boton} text-white`}>
              {icono}
            </div>
            {/*
              El "cómo se usa". Chico y en la esquina a propósito: tiene que estar ahí sin competir
              con la acción, que es lo que se aprieta cien veces al día.
            */}
            <button
              type="button"
              onClick={() => setVolteada(true)}
              className={`flex h-9 w-9 items-center justify-center rounded-lg border ${acento.borde} bg-white/70 ${acento.texto} hover:bg-white`}
              aria-label={`Cómo se usa: ${titulo}`}
              title="¿Cómo se usa?"
            >
              <HelpCircle className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-2 flex-1">
            <div className={`text-base font-extrabold leading-tight ${acento.texto}`}>{titulo}</div>
            <p className="mt-0.5 text-xs leading-snug text-slate-600">{descripcion}</p>
          </div>

          <button
            type="button"
            onClick={onAccion}
            className={`mt-2 min-h-[48px] w-full rounded-xl text-base font-bold text-white ${acento.boton}`}
          >
            {textoAccion}
          </button>
          {pie && <div className="mt-1.5 text-center text-xs text-slate-600">{pie}</div>}
          {alPie && (
            <button
              type="button"
              onClick={() => void alPie.onClick()}
              className="mt-1 w-full text-center text-xs text-slate-500 underline decoration-dotted hover:text-slate-700"
            >
              {alPie.texto}
            </button>
          )}
        </div>

        {/* ─── Reverso: cómo se usa ─── */}
        <div
          className={`absolute inset-0 flex flex-col overflow-auto rounded-2xl border-2 bg-white p-3 [backface-visibility:hidden] [transform:rotateY(180deg)] ${acento.borde}`}
          aria-hidden={!volteada}
        >
          <div className="flex items-start justify-between gap-2">
            <div className={`text-sm font-bold uppercase tracking-wide ${acento.texto}`}>
              Cómo se usa
            </div>
            <button
              type="button"
              onClick={() => setVolteada(false)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
              aria-label="Volver"
              title="Volver"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/*
            Los pasos van numerados y en el orden real, **incluidos los que ocurren fuera del
            sistema**. Ahí está el valor: "pégalo en el chat" no es algo que la pantalla pueda
            mostrar, y es justo donde la gente se queda.
          */}
          <ol className="mt-2 flex-1 space-y-1.5 overflow-auto text-sm text-slate-700">
            {comoSeUsa.map((paso, i) => (
              <li key={i} className="flex gap-2">
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${acento.boton}`}
                >
                  {i + 1}
                </span>
                <span>{paso}</span>
              </li>
            ))}
          </ol>

          <button
            type="button"
            onClick={() => setVolteada(false)}
            className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-300 text-sm font-semibold text-slate-700"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
